import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil, Plus } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { EntityViewLayout } from '../../components';
import { useSupplier } from '../../hooks/useSuppliers';

interface Supplier {
  id: number;
  name: string;
  address?: string;
  countryId?: number;
  countryName?: string;
  regionId?: number;
  regionName?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  description?: string;
  vineyards: number;
  bulkWine: number;
  cleanskins: number;
  fruits: number;
  users: number;
  dateCreated: string;
}

export const SupplierDetailPage: React.FC = () => {
  const showCleanskinsCard = false;
  const showFruitsCard = false;
  const showVineyardsCard = false;

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: loading } = useSupplier(id as string, Boolean(id));
  const supplier: Supplier | null = data
    ? {
        id: data.id,
        name: data.name,
        address: data.address,
        countryId: data.countryId,
        countryName: data.country?.name,
        regionId: data.regionId,
        regionName: data.region?.name,
        contactName: data.contactName,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        website: data.website,
        description: data.description,
        vineyards: 0,
        bulkWine: 0,
        cleanskins: 0,
        fruits: 0,
        users: 0,
        dateCreated: data.createdAt,
      }
    : null;

  if (loading) {
    return (
      <div>Loading...</div>
    );
  }

  if (!supplier) {
    return (
      <div>Supplier not found</div>
    );
  }

  return (
    <EntityViewLayout
      closeTo="/suppliers"
      backTo="/suppliers"
      backLabel="Back to suppliers"
      title={supplier.name}
      headerAction={
        <button
          onClick={() => navigate(`/suppliers/${id}/edit`)}
          className="flex items-center gap-2 hover:opacity-90 transition-opacity"
          style={{
            height: '38px',
            borderRadius: '6px',
            backgroundColor: '#404040',
            border: '1px solid #404040',
            padding: '0 16px',
            color: '#EAECF0',
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 400,
          }}
        >
          <Pencil size={20} color="#EAECF0" strokeWidth={1.67} aria-hidden="true" />
          Edit
        </button>
      }
    >

        {/* Cards Grid */}
        <div className="grid grid-cols-3 gap-6">
          {/* Bulk Wine Card */}
          <Card>
            {supplier.bulkWine === 0 ? (
              <button
                onClick={() => navigate(`/bulk-wines/new?supplierId=${supplier.id}`)}
                className="w-full h-full flex flex-col items-center justify-center hover:opacity-70"
                style={{ minHeight: '100px' }}
              >
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#404040',
                  letterSpacing: '0.05em',
                  marginBottom: '8px'
                }}>
                  BULK WINE
                </div>
                <Plus size={24} color="#404040" strokeWidth={2} className="mb-2" aria-hidden="true" />
                <span style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#404040'
                }}>
                  Add new
                </span>
              </button>
            ) : (
              <button
                onClick={() => navigate(`/bulk-wines/new?supplierId=${supplier.id}`)}
                className="w-full hover:opacity-70"
              >
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#404040',
                  letterSpacing: '0.05em',
                  marginBottom: '8px'
                }}>
                  BULK WINE
                </div>
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '32px',
                  fontWeight: 400,
                  color: '#404040'
                }}>
                  {supplier.bulkWine}
                </div>
              </button>
            )}
          </Card>

          {showCleanskinsCard && (
            <Card>
              {supplier.cleanskins === 0 ? (
                <button
                  className="w-full h-full flex flex-col items-center justify-center hover:opacity-70"
                  style={{ minHeight: '100px' }}
                >
                  <div style={{
                    fontFamily: 'Oxygen',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#404040',
                    letterSpacing: '0.05em',
                    marginBottom: '8px'
                  }}>
                    CLEANSKINS
                  </div>
                  <Plus size={24} color="#404040" strokeWidth={2} className="mb-2" aria-hidden="true" />
                  <span style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#404040'
                  }}>
                    Add new
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/suppliers/${supplier.id}/cleanskins`)}
                  className="w-full hover:opacity-70"
                >
                  <div style={{
                    fontFamily: 'Oxygen',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#404040',
                    letterSpacing: '0.05em',
                    marginBottom: '8px'
                  }}>
                    CLEANSKINS
                  </div>
                  <div style={{
                    fontFamily: 'Oxygen',
                    fontSize: '32px',
                    fontWeight: 400,
                    color: '#404040'
                  }}>
                    {supplier.cleanskins}
                  </div>
                </button>
              )}
            </Card>
          )}

          {showFruitsCard && (
            <Card>
              {supplier.fruits === 0 ? (
                <button
                  className="w-full h-full flex flex-col items-center justify-center hover:opacity-70"
                  style={{ minHeight: '100px' }}
                >
                  <div style={{
                    fontFamily: 'Oxygen',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#404040',
                    letterSpacing: '0.05em',
                    marginBottom: '8px'
                  }}>
                    FRUITS
                  </div>
                  <Plus size={24} color="#404040" strokeWidth={2} className="mb-2" aria-hidden="true" />
                  <span style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#404040'
                  }}>
                    Add new
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/suppliers/${supplier.id}/fruits`)}
                  className="w-full hover:opacity-70"
                >
                  <div style={{
                    fontFamily: 'Oxygen',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#404040',
                    letterSpacing: '0.05em',
                    marginBottom: '8px'
                  }}>
                    FRUITS
                  </div>
                  <div style={{
                    fontFamily: 'Oxygen',
                    fontSize: '32px',
                    fontWeight: 400,
                    color: '#404040'
                  }}>
                    {supplier.fruits}
                  </div>
                </button>
              )}
            </Card>
          )}
          {showVineyardsCard && (
            <Card>
              {supplier.vineyards === 0 ? (
                <button
                  className="w-full h-full flex flex-col items-center justify-center hover:opacity-70"
                  style={{ minHeight: '100px' }}
                >
                  <div style={{
                    fontFamily: 'Oxygen',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#404040',
                    letterSpacing: '0.05em',
                    marginBottom: '8px'
                  }}>
                    VINEYARDS
                  </div>
                  <Plus size={24} color="#404040" strokeWidth={2} className="mb-2" aria-hidden="true" />
                  <span style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#404040'
                  }}>
                    Add new
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/suppliers/${supplier.id}/vineyards`)}
                  className="w-full hover:opacity-70"
                >
                  <div style={{
                    fontFamily: 'Oxygen',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#404040',
                    letterSpacing: '0.05em',
                    marginBottom: '8px'
                  }}>
                    VINEYARDS
                  </div>
                  <div style={{
                    fontFamily: 'Oxygen',
                    fontSize: '32px',
                    fontWeight: 400,
                    color: '#404040'
                  }}>
                    {supplier.vineyards}
                  </div>
                </button>
              )}
            </Card>
          )}

          {/* Users Card */}
          <Card>
            {supplier.users === 0 ? (
              <button
                onClick={() => navigate(`/users?invite=true&supplierId=${supplier.id}`)}
                className="w-full h-full flex flex-col items-center justify-center hover:opacity-70"
                style={{ minHeight: '100px' }}
              >
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#404040',
                  letterSpacing: '0.05em',
                  marginBottom: '8px'
                }}>
                  USERS
                </div>
                <Plus size={24} color="#404040" strokeWidth={2} className="mb-2" aria-hidden="true" />
                <span style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#404040'
                }}>
                  Invite
                </span>
              </button>
            ) : (
              <button
                onClick={() => navigate(`/users?invite=true&supplierId=${supplier.id}`)}
                className="w-full hover:opacity-70"
              >
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#404040',
                  letterSpacing: '0.05em',
                  marginBottom: '8px'
                }}>
                  USERS
                </div>
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '32px',
                  fontWeight: 400,
                  color: '#404040'
                }}>
                  {supplier.users}
                </div>
              </button>
            )}
          </Card>
        </div>

        {/* Basic Information Section */}
        <Card className="mt-6">
          <h2 style={{
            fontFamily: 'Oxygen',
            fontSize: '18px',
            fontWeight: 700,
            color: '#101828',
            marginBottom: '24px'
          }}>
            Basic Information
          </h2>
          
          <div className="space-y-4">
            {/* Address */}
            <div style={{
              fontFamily: 'Oxygen',
              fontSize: '16px',
              fontWeight: 400,
              color: '#667085',
              lineHeight: '20px'
            }}>
              <span style={{ fontWeight: 400 }}>Address: </span>{supplier.address || ''}
            </div>

            {/* Country */}
            <div style={{
              fontFamily: 'Oxygen',
              fontSize: '16px',
              fontWeight: 400,
              color: '#667085',
              lineHeight: '20px'
            }}>
              <span style={{ fontWeight: 400 }}>Country: </span>{supplier.countryName || ''}
            </div>

            {/* Region */}
            <div style={{
              fontFamily: 'Oxygen',
              fontSize: '16px',
              fontWeight: 400,
              color: '#667085',
              lineHeight: '20px'
            }}>
              <span style={{ fontWeight: 400 }}>Region: </span>{supplier.regionName || ''}
            </div>

            {/* Supplier Contact Name */}
            <div style={{
              fontFamily: 'Oxygen',
              fontSize: '16px',
              fontWeight: 400,
              color: '#667085',
              lineHeight: '20px'
            }}>
              <span style={{ fontWeight: 400 }}>Supplier Contact Name: </span>{supplier.contactName || ''}
            </div>

            {/* Email Address */}
            <div style={{
              fontFamily: 'Oxygen',
              fontSize: '16px',
              fontWeight: 400,
              color: '#667085',
              lineHeight: '20px'
            }}>
              <span style={{ fontWeight: 400 }}>Email Address: </span>{supplier.contactEmail || ''}
            </div>

            {/* Phone Number */}
            <div style={{
              fontFamily: 'Oxygen',
              fontSize: '16px',
              fontWeight: 400,
              color: '#667085',
              lineHeight: '20px'
            }}>
              <span style={{ fontWeight: 400 }}>Phone Number: </span>{supplier.contactPhone || ''}
            </div>
          </div>
        </Card>
      </EntityViewLayout>
  );
};

export default SupplierDetailPage;
